// ═══════════════════════════════════════════════════════════════════════════════
// Verify email from link (public)
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { appButtonClassName } from '../../components/ui/AppButton';
import { useLanguage } from '../../contexts/LanguageContext';
import { api } from '../../lib/api';
import { PLAY_BANNER_ABSOLUTE_TOP_4_CLASS, PLAY_BANNER_OFFSET_PADDING_CLASS } from '../../lib/layoutChrome';

export const VerifyEmailPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'fail'>('idle');
  const verifyOnceRef = useRef(false);

  const runVerify = useCallback(async () => {
    if (!token) {
      setStatus('fail');
      return;
    }
    setStatus('loading');
    try {
      const response = await api.post<{ message?: string }>('/auth/verify-email', { token });
      if (!response.success) {
        throw new Error(response.error ?? t('auth.verifyFail'));
      }
      setStatus('ok');
    } catch {
      setStatus('fail');
    }
  }, [token, t]);

  useEffect(() => {
    // Avoid duplicate POST in React Strict Mode (dev) and double effect runs
    if (verifyOnceRef.current) {
      return;
    }
    verifyOnceRef.current = true;
    runVerify();
  }, [runVerify]);

  return (
    <div className={`relative flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-10 ${PLAY_BANNER_OFFSET_PADDING_CLASS}`}>
      <div className={`absolute right-4 z-10 flex flex-col items-end gap-2 ${PLAY_BANNER_ABSOLUTE_TOP_4_CLASS}`}>
        <LanguageSwitcher />
        <Link
          to="/login"
          className="text-sm font-medium text-primary-600 underline"
        >
          {t('auth.goToLogin')}
        </Link>
      </div>
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
        <h1 className="text-2xl font-bold text-gray-900" id="verify-heading">
          {t('auth.verifyTitle')}
        </h1>
        <div className="mt-6" role="status" aria-live="polite" aria-labelledby="verify-heading">
          {status === 'loading' ? (
            <p className="text-gray-600">{t('auth.verifyPending')}</p>
          ) : null}
          {status === 'ok' ? (
            <p className="text-green-700">{t('auth.verifySuccess')}</p>
          ) : null}
          {status === 'fail' ? (
            <p className="text-red-700">{token ? t('auth.verifyFail') : t('auth.missingToken')}</p>
          ) : null}
        </div>
        {status === 'ok' || status === 'fail' ? (
          <Link
            to="/login"
            className={appButtonClassName({ variant: 'primary', className: 'mt-6 shadow-sm' })}
          >
            {t('auth.goToLogin')}
          </Link>
        ) : null}
      </div>
    </div>
  );
};
